<template>
  <div>
    <Transition name="slide-left-right">
      <div v-if="!showTestingForm">
        <b-row class="pt-2 pb-1">
          <b-col cols="6" class="d-flex align-items-center">
            <BreadcrumbMenu />
          </b-col>
          <b-col cols="6" class="pr-3 text-right">
            <button-filters
              @show-modal="$root.$emit('bv::show::modal', $event)"
              :filters-count="filtersCount"
              @clear-search-filters="clearSearchFilters"
            />
          </b-col>
        </b-row>

        <filter-modal @action="buildFilters()" @clear="clearSearchFilters()">
          <template v-slot:main-filters>
            <filters
              :unit-options="unitOptions"
              :unit-filter.sync="filters.units"
              :user-options="userOptions"
              :country-options="countryOptions"
              :municipality-options="municipalityOptions"
              :status-options="statusOptions"
              :user-filter.sync="filters.users"
              :status-filter.sync="filters.status"
              :responsable-filter.sync="filters.responsable"
              :assigned-filter.sync="filters.assigned"
              :start-date.sync="filters.startDate"
              :end-date.sync="filters.endDate"
              :country-filter.sync="filters.countries"
              :municipality-filter.sync="filters.municipalities"
              :rems-filter.sync="filters.rems"
              :sirh-filter.sync="filters.sirh"
            />
          </template>
        </filter-modal>

        <b-card class="mx-2" no-body>
          <b-row class="m-2">
            <b-col
              cols="12"
              md="6"
              class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
            >
              <label class="text-capitalize">{{ $t("table.show") }}</label>
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block mx-50"
              />
              <label class="text-capitalize">{{ $t("table.entries") }}</label>
            </b-col>

            <b-col cols="12" md="6">
              <div class="d-flex align-items-center justify-content-end">
                <b-form-input
                  v-model="searchQuery"
                  class="d-inline-block mr-1"
                  :placeholder="$t('table.search') + '...'"
                />
                <b-button
                  variant="primary"
                  @click="openPenetrationTesting()"
                  v-if="$can('create', 'Testing')"
                >
                  <span class="text-nowrap text-capitalize">{{
                    $t("actions.add") + " " + $tc("testing.penetration.title", 1)
                  }}</span>
                </b-button>
                <b-button
                  @click="exportExcel()"
                  variant="success"
                  class="text-capitalize ml-1"
                  type="button"
                  :disabled="buttonExport"
                >
                  <feather-icon icon="DownloadCloudIcon" size="15" />
                </b-button>
              </div>
            </b-col>
          </b-row>

          <b-table
            ref="refListTable"
            class="position-relative"
            :items="fetchList"
            responsive
            :fields="tableColumns"
            primary-key="id"
            :sort-by.sync="sortBy"
            show-empty
            :empty-text="$t('no_record_found')"
            :sort-desc.sync="isSortDirDesc"
            selectable
            select-mode="single"
            @row-selected="onRowSelected"
          >
            <template #head()="data">
              <span> {{ $tc(data.label, 1) }}</span>
            </template>

            <template #head(actions)="data">
              <span> {{ $tc(data.label, 2) }}</span>
            </template>

            <template #cell(reference_number)="data">
              <p class="mb-0">{{ data.item.reference_number }}</p>
              <b-badge :variant="getStatusVariant(data.item.last_status)">
                {{ formatStatus(data.item.last_status) }}
              </b-badge>
            </template>

            <template #cell(assiged_to)="data">
              <p class="mb-0">
                {{ getUserAssigned(data.item.last_status) }}
              </p>
            </template>

            <template #cell(in_review_by)="data">
              <p class="mb-0">
                {{ getUserReview(data.item.penetration_testing_status) }}
              </p>
            </template>

            <template #cell(reviewed_to)="data">
              <p class="mb-0">
                {{ getUserValidator(data.item.last_status) }}
              </p>
            </template>

            <template #cell(type)="data">
              <p class="mb-0">{{ $t('testing.penetration.title') }}</p>
            </template>

            <template #cell(created_at)="data">
              <span>{{ formatDateAssigned(data.item.created_at) }}</span>
            </template>

            <template v-if="$can('update', 'Testing') || $can('cancel', 'Testing')" #cell(actions)="data">
              <b-dropdown variant="link" no-caret :right="$store.state.appConfig.isRTL">
                <template #button-content>
                  <feather-icon
                    icon="MoreVerticalIcon"
                    size="16"
                    class="align-middle text-body"
                  />
                </template>

                <b-dropdown-item
                  v-if="$can('update', 'Testing')"
                  @click="openPenetrationTesting(data.item, true)"
                >
                  <feather-icon icon="EditIcon" />
                  <span class="align-middle ml-50 text-capitalize">{{
                    $t("actions.edit")
                  }}</span>
                </b-dropdown-item>

                <b-dropdown-item
                  v-if="$can('cancel', 'Testing') && data.item.last_status && (data.item.last_status.status.name !== 'approved' && data.item.last_status.status.name !== 'canceled')"
                  @click="confirmDelete(data.item.id)"
                >
                  <feather-icon icon="TrashIcon" />
                  <span class="align-middle ml-50 text-capitalize">
                    {{ $t("actions.cancel") }}
                  </span>
                </b-dropdown-item>

                <b-dropdown-item
                  v-if="data.item.last_status && data.item.last_status.status.name == 'approved'"
                  @click="downloadPdfAndFilesLinear(data.item.id)"
                >
                  <feather-icon icon="FileIcon" />
                  <span class="align-middle ml-50 text-capitalize">{{
                    $t("actions.download")
                  }}</span>
                </b-dropdown-item>

              </b-dropdown>
            </template>
          </b-table>

          <div class="mx-2 mb-2">
            <b-row>
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-start"
              >
                <span class="text-muted text-capitalize">
                  {{
                    $t("table.showing", {
                      from: dataMeta.from,
                      to: dataMeta.to,
                      of: dataMeta.of,
                    })
                  }}
                </span>
              </b-col>
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-end"
              >
                <Pagination
                  :totalList="totalList"
                  :currentPage="currentPage"
                  @updatePagination="update"
                  :perPage="perPage"
                />
              </b-col>
            </b-row>
          </div>
        </b-card>
      </div>
    </Transition>
    <Transition name="slide-right-left">
      <div class="p-1" v-if="showTestingForm">
        <PenetrationForm
          :isEdit="isEdit"
          :dataToEdit="dataToEdit"
          @goBackToList="goBackToList()"
        />
      </div>
    </Transition>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BDropdown,
  BDropdownItem,
  BTable,
} from "bootstrap-vue";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import { ref, getCurrentInstance, onMounted, watch, computed } from "@vue/composition-api";
import vSelect from "vue-select";
import Swal from "sweetalert2";
import axios from "@/libs/axios";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import Pagination from "@/components/Pagination.vue";

import useList from "./useList";

import ShowSimulacrumFormModal from "@/components/ShowSimulacrumFormModal.vue";
import Filters from "@/views/testing/Filters.vue";
import FilterModal from "@/components/Filters/FilterModal.vue";
import ButtonFilters from "@/components/Filters/ButtonFilters.vue";
import PenetrationForm from "./PenetrationForm.vue";

export default {
  name: "IndexPenetrationTesting",
  components: {
    BreadcrumbMenu,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BDropdown,
    BDropdownItem,
    BTable,
    Pagination,
    vSelect,
    ToastificationContent,
    ShowSimulacrumFormModal,
    Filters,
    FilterModal,
    ButtonFilters,
    PenetrationForm
  },
  setup(_, { emit }) {
    const vm = getCurrentInstance().proxy;

    const {
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,
      unitOptions,
      userOptions,
      filters,
      filtersCount,
      buildFilters,
      countryOptions,
      municipalityOptions,
      clearSearchFilters,
      userSelected,
      getUserAssigned,
      getUserReview,
      getUserValidator
    } = useList();

    const toast = useToast();

    // const modalId = ref(null);
    // const modalSnapshot = ref(null);
    // const modalSources = ref(null);
    const buttonExport = ref(false);
    const showTestingForm = ref(false);
    const isEdit = ref(false);
    const dataToEdit = ref(null);

    const statusOptions = [
      { label: vm.$t("risk.status_in_progress"), value: "in_progress" },
      { label: vm.$t("risk.status_in_review"), value: "in_review" },
      { label: vm.$t("risk.status_approved"), value: "approved" },
      { label: vm.$t("risk.status_rejected"), value: "rejected" },
      { label: vm.$t("risk.status_reassigned"), value: "reopened" },
      { label: vm.$t("risk.status_canceled"), value: "canceled" }
    ];

    const openPenetrationTesting = (row, edit) => {
      if(edit) {
        isEdit.value = true;
        dataToEdit.value = row;
      }
      showTestingForm.value = true;
    };

    const reopenPenetrationTesting = (row) => {
      isEdit.value = true;
      dataToEdit.value = row;
      showTestingForm.value = true;
    };

    const onRowSelected = (row) => {
      const [data] = row;

      reopenPenetrationTesting(data);
    }

    const goBackToList = async () => {
      showTestingForm.value = false;
      isEdit.value = false;
      dataToEdit.value = null;
      vm.$router.push({path: '/testing/penetration'})
    };

    const getQueryParam = (param) => {
      if (vm.$route && vm.$route.query && vm.$route.query[param])
        return vm.$route.query[param];
      const queryString = window.location.hash.split("?")[1];
      if (!queryString) return null;
      const params = new URLSearchParams(queryString);
      return params.get(param);
    };

    async function openFromNotification() {
      const idParam = getQueryParam("id");

      if (!idParam) return;

      await axios.get(`v1/testing/penetration/${idParam}`)
        .then(response => {
          const { data } = response;

          dataToEdit.value = data.data;
        })
        .catch(error => {
          dataToEdit.value = null;
        });

      showTestingForm.value = true;

      /* modalId.value = Number(idParam);
      modalSnapshot.value = null;
      modalSources.value = null;
      await vm.$nextTick();
      vm.$root.$emit("bv::show::modal", "simulacrum_form_modal"); */
    }

    watch(
      () => (vm.$route && vm.$route.query ? vm.$route.query.id : null),
      async (newId, oldId) => {
        if (newId && newId !== oldId) await openFromNotification();
      }
    );

    const confirmDelete = (id) => {
      Swal.fire({
        title: "¿Estás seguro de que deseas cancelar esta prueba de penetración?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: vm.$t("actions.cancel") || "Cancelar",
        cancelButtonText: vm.$t("actions.exit") || "Salir",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          axios
            .get(`/v1/testing/penetration/${id}/canceled`)
            .then(() => {
              refetchData();
              toast({
                component: ToastificationContent,
                props: {
                  title: "Prueba de penetración cancelada",
                  icon: "CheckIcon",
                  variant: "success",
                },
              });
            });
        }
      });
    };

    const update = (val) => {
      currentPage.value = val;
    };

    onMounted(() => {
      openFromNotification();
    });

    // function onWizardComplete() {
    //   refetchData();
    // }

    const formatStatus = (lastStatus) => {
      const name = (lastStatus) ? lastStatus.status.name : 'in_progress';
      const raw = String(name || "");
      const norm = raw
        .trim()
        .toLowerCase()
        .replace(/[-\s]+/g, "_");
      const aliases = { canceled: "cancelled" };
      const canonical = aliases[norm] || norm;
      const tryKeys = [
        `procedure_states.${canonical}`,
        `procedure_states.value.${canonical}`,
      ];
      for (const k of tryKeys) {
        const v = vm.$t ? vm.$t(k) : k;
        if (typeof v === "string" && v !== k) return v;
      }
      return raw;
    };

    const safe = (s) =>
      String(s || "")
        .replace(/[\/\\:*?"<>|]+/g, "_")
        .replace(/\s+/g, " ")
        .trim();

    const delay = (ms) => new Promise((r) => setTimeout(r, ms));

    function saveBlob(blob, filename) {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }

    async function downloadOne(url, filename) {
      try {
        const res = await axios.get(url, { responseType: "blob" });
        const name = safe(filename || url.split("/").pop() || "archivo");
        saveBlob(res.data, name);
      } catch (e) {
        const a = document.createElement("a");
        a.href = url;
        a.target = "_blank";
        a.rel = "noopener";
        a.click();
      }
    }

    async function fetchPdfPenetrationTesting(id) {
      const res = await axios.get(`/v1/testing/penetration/${id}/pdf`, {
        params: {  },
        responseType: "blob",
        headers: { Accept: "application/pdf" },
      });

      const cd = res.headers["content-disposition"];
      let filename = `prueba_penetracion_${id}.pdf`;

      if (cd) {
        const m = cd.match(/filename=\"?([^\";]+)\"?/i);

        if (m) filename = decodeURIComponent(m[1]);
      }

      return { blob: res.data, filename: safe(filename) };
    }

    async function fetchDetailsForFilesSimulacrum(evaluationId) {
      const res = await axios.get(
        `/v1/simulacrum_form_answers/get-form-by-simulacrum/${evaluationId}`
      );
      return res.data || {};
    }

    function extractAllFileEntriesSimulacrum(payload) {
      let answers = payload;

      if (answers && typeof answers === "object" && answers.data) {
        answers = answers.data;
      }
      if (answers && typeof answers === "object" && answers.data) {
        answers = answers.data;
      }

      const out = [];
      if (!answers || typeof answers !== "object") return out;

      Object.entries(answers).forEach(([fieldName, value]) => {
        if (!Array.isArray(value)) return;

        value.forEach((file, idx) => {
          if (!file || !file.url) return;
          const url = file.url;
          const baseName = file.name || file.filename || `archivo_${idx + 1}`;
          const niceName = `${safe(fieldName)} - ${safe(baseName)}`;
          out.push({ url, name: niceName });
        });
      });

      return out;
    }

    async function downloadPdfAndFilesLinear(id) {
      try {
        const { blob: pdfBlob, filename: pdfName } = await fetchPdfPenetrationTesting(id);

        saveBlob(pdfBlob, pdfName);

        await delay(400);
      } catch (error) {
        toast({
            component: ToastificationContent,
            props: {
              title: vm.$t("errors.download_failed") || "No se pudo descargar",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
      }
    }

    const downloadPdf = async (evaluationId) => {
      try {
        const res = await axios.get(`/v1/simulacrums/${evaluationId}`, {
          params: { is_pdf: true },
          responseType: "blob",
          headers: { Accept: "application/pdf" },
        });

        const cd = res.headers["content-disposition"];
        let filename = `simulacrum_${evaluationId}.pdf`;
        if (cd) {
          const m = cd.match(/filename=\"?([^\";]+)\"?/i);
          if (m) filename = decodeURIComponent(m[1]);
        }

        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("error", error);
      }
    };

    const exportExcel = async () => {
      try {
        buttonExport.value = true;
        const responseType = "blob";
        const response = await axios.get('v1/testing/penetration/export', {
          params: { filters:filters.value },
          responseType
        });

        const url = window.URL.createObjectURL(
          new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          })
        );

        const filename = response.headers["content-disposition"].split("filename=")[1];

        const link = document.createElement("a");

        link.href = url;

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();

        const notification = {
          id: filename,
          progress: 100,
          completed: true,
          view: "users",
          url: url,
          download: filename,
          title: "Pruebas de pénetración",
          body: "Descarga completa",
          error: false,
          timestamp:
            new Date().toLocaleDateString() + " " + new Date().toLocaleTimeString(),
        };

        store.dispatch("grid/updateDownloadNotification", notification);
      } catch (error) {
        const { statusText } = error.response;

        toast({
          component: ToastificationContent,
          props: {
            title: statusText,
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        buttonExport.value = false;
      }
    }

    // Helpers to normalize user-like values that can be either string or object
    const getUserPrimary = (val) => {
      if (!val) return "-";
      if (typeof val === "string") return val || "-";
      return val.name || val.username || val.email || "-";
    };
    const getUserSecondary = (val) => {
      if (!val) return "";
      if (typeof val === "string") return "";
      return val.username || val.email || "";
    };

    /* const disabledEdit = (status) => {
      const statuses = ['rejected','approved','in_review'];

      return statuses.includes(status);
    } */

    const disabledReopen = (status) => {
      return (status !== 'approved');
    }

    const getStatusVariant = (lastStatus) => {
      if (!lastStatus) return "primary";

      const { name } = lastStatus.status;
      const statusVariants = {
        in_progress: "primary",
        pending: "warning",
        in_review: "info",
        approved: "success",
        rejected: "danger",
        reopened: "warning",
      }

      return statusVariants[name] || "secondary";
    };

    return {
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      isEdit,
      dataToEdit,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      unitOptions,
      userOptions,
      filters,
      filtersCount,
      countryOptions,
      municipalityOptions,
      buttonExport,
      statusOptions,
      showTestingForm,
      fetchList,
      refetchData,
      formatDateAssigned,
      buildFilters,
      clearSearchFilters,
      confirmDelete,
      update,
      openPenetrationTesting,
      formatStatus,
      getUserPrimary,
      getUserSecondary,
      exportExcel,
      downloadPdf,
      downloadPdfAndFilesLinear,
      goBackToList,
      userSelected,
      getStatusVariant,
      disabledReopen,
      reopenPenetrationTesting,
      onRowSelected,
      getUserAssigned: getUserAssigned,
      getUserReview,
      getUserValidator
    };
  },
};
</script>

<style>
.slide-right-left-enter-active,
.slide-right-left-leave-active {
  transition: transform 0.2s ease;
}

.slide-right-left-enter-from {
  transform: translateX(100%);
}

.slide-right-left-enter-to {
  transform: translateX(0%);
}

.slide-right-left-leave-from {
  transform: translateX(0%);
}

.slide-right-left-leave-to {
  transform: translateX(100%);
}

.slide-left-right-enter-active,
.slide-left-right-leave-active {
  transition: transform 0.2s ease;
}

.slide-left-right-enter-from {
  transform: translateX(-100%);
}

.slide-left-right-enter-to {
  transform: translateX(0%);
}

.slide-left-right-leave-from {
  transform: translateX(0%);
}

.slide-left-right-leave-to {
  transform: translateX(-100%);
}

</style>

